// Dashboards and reports (milestone 11): types and calls.
import { requestRaw, type CelDiagnostic, type ExprCheck } from './api'

export type WidgetKind = 'line' | 'bar' | 'stacked' | 'area' | 'table' | 'stat' | 'map'

export interface NamedExpr {
  name: string
  expr: string
}
export interface Measure extends NamedExpr {
  format?: 'number' | 'integer' | 'percent' | 'sek' | 'compact'
}
export interface GridPos {
  x: number
  y: number
  w: number
  h: number
}
export interface WidgetStyle {
  colors?: string[]
  x_label?: string
  y_label?: string
  hide_legend?: boolean
  horizontal?: boolean
  show_values?: boolean
  smooth?: boolean
  trend?: boolean
}
export interface Widget {
  id: string
  title: string
  kind: WidgetKind
  bucket: string
  dimensions?: NamedExpr[]
  measures?: Measure[]
  where?: string
  sort?: { name: string; desc?: boolean }[]
  limit?: number
  layout: GridPos
  style?: WidgetStyle
  map?: { level: 'lan' | 'kommun'; key: 'code' | 'name' }
  ignore_filters?: string[]
}
export interface DashboardFilter {
  id: string
  label: string
  kind: 'date_range' | 'select'
  field: string
  default?: string
}
export interface DashboardSpec {
  title: string
  description?: string
  palette?: string
  filters?: DashboardFilter[]
  widgets: Widget[]
  refresh_seconds?: number
}
export interface ReportSpec {
  title: string
  dashboard: string
  schedule: string
  recipients: string[]
  format: 'html' | 'pdf'
  filters?: Record<string, string>
  intro?: string
  enabled: boolean
}
export interface DashSummary {
  kind: 'dashboard' | 'report'
  slug: string
  title: string
  status: string
  org_id: string
  current_version: number
  updated_at: string
  widgets: number
  dashboard?: string
  schedule?: string
}
export interface Problem {
  widget?: string
  path: string
  message: string
  diagnostics?: CelDiagnostic[]
}
export interface DashCheck {
  ok: boolean
  problems: Problem[]
  columns?: Record<string, { name: string; role: string; type: string; format?: string }[]>
}
export interface DashVersion {
  version: number
  body: unknown
  comment: string
  created_at: string
  created_by: string
}
export interface ChartableBucket {
  slug: string
  title: string
  org_id: string
  columns: { name: string; type: string; description?: string }[]
}
export interface ReportSend {
  recipient: string
  status: 'sent' | 'skipped' | 'failed'
  detail: string
  created_at: string
  version: number
}

export interface Template {
  id: string
  title: string
  description: string
  schedule: string
  needs: ('date' | 'category' | 'region')[]
}
export interface TemplateParams {
  template: string
  title: string
  bucket: string
  date: string
  category?: string
  region?: string
  region_level?: 'lan' | 'kommun'
  region_key?: 'code' | 'name'
  measure?: string
  measure_name?: string
}

export type Values = Record<string, string>
/** A saved dashboard (slug) or the builder's draft (spec). */
export type Source = { slug: string; version?: number } | { spec: DashboardSpec }

async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
  return (await requestRaw(method, path, body)).json() as Promise<T>
}

export const dash = {
  list: (kind: 'dashboard' | 'report') => json<DashSummary[]>('GET', `/${kind}s`),
  get: (kind: 'dashboard' | 'report', slug: string) =>
    json<{ summary: DashSummary; versions: DashVersion[]; check: DashCheck | null }>('GET', `/${kind}s/${slug}`),
  create: (kind: 'dashboard' | 'report', slug: string, spec: unknown, comment = '') =>
    json<{ slug: string; version: number }>('POST', `/${kind}s`, { slug, spec, comment }),
  save: (kind: 'dashboard' | 'report', slug: string, spec: unknown, comment = '') =>
    json<{ slug: string; version: number }>('POST', `/${kind}s/${slug}/spec`, { spec, comment }),
  check: (kind: 'dashboard' | 'report', spec: unknown) => json<DashCheck>('POST', `/${kind}s/check`, { spec }),
  archive: (kind: 'dashboard' | 'report', slug: string) => json<{ archived: boolean }>('POST', `/${kind}s/${slug}/archive`, {}),
  buckets: () => json<ChartableBucket[]>('GET', '/dashboards/buckets'),
  expr: (bucket: string, expression: string) => json<ExprCheck>('POST', '/dashboards/expr', { bucket, expression }),
  options: (src: Source, filter: string) => json<string[]>('POST', '/dashboards/options', { ...src, filter }),
  freshness: (slug: string) => json<Record<string, number>>('GET', `/dashboards/${slug}/freshness`),
  svg: async (src: Source, widget: string, width: number, height: number, theme: 'light' | 'dark', values: Values) => {
    const res = await requestRaw('POST', '/dashboards/render', { ...src, widget, width, height, theme, values })
    return { svg: await res.text(), dataAt: res.headers.get('X-Data-At') ?? '', truncated: res.headers.get('X-Truncated') === 'true' }
  },
  png: async (src: Source, widget: string, width: number, height: number, values: Values) =>
    (await requestRaw('POST', '/dashboards/render', { ...src, widget, width, height, theme: 'light', values, format: 'png' })).blob(),
  pdf: async (src: Source, values: Values, locale: 'en' | 'sv') => (await requestRaw('POST', '/dashboards/export', { ...src, values, locale })).blob(),
  templates: () => json<Template[]>('GET', '/dashboards/templates'),
  fromTemplate: (p: TemplateParams & { slug: string }) => json<{ slug: string; version: number }>('POST', '/dashboards/from-template', p),
  sendReport: (slug: string) => json<{ run_id: number }>('POST', `/reports/${slug}/send`, {}),
  reportSends: (slug: string) => json<ReportSend[]>('GET', `/reports/${slug}/sends`),
  preview: async (slug: string) => (await requestRaw('POST', `/reports/${slug}/preview`, {})).text(),
}

/** Saves a blob as a download. */
export function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const datePresets: { value: string; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'last_12_months', label: 'Last 12 months' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
]

export const widgetKinds: { value: WidgetKind; label: string }[] = [
  { value: 'line', label: 'Line' },
  { value: 'area', label: 'Area' },
  { value: 'bar', label: 'Bar' },
  { value: 'stacked', label: 'Stacked bar' },
  { value: 'stat', label: 'Single value' },
  { value: 'table', label: 'Table' },
  { value: 'map', label: 'Map (län/kommun)' },
]

export const palettes = ['aleva', 'nordic', 'warm', 'mono', 'colorblind']
